import React, { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function LeaderboardManagement() {
    const [leaderboards, setLeaderboards] = useState([]);
    const [events, setEvents] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [newLeaderboard, setNewLeaderboard] = useState({ 
        CompetitionID: '', 
        RefreshInterval: 30, 
        Enabled: true,
        // Add column visibility settings with default values
        ShowCompetitorID: true,
        ShowCompetitorName: true,
        ShowOrganization: true,
        ShowCompetitorState: true,
        ShowDivision: true,
        ShowAttempt: true,
        ShowTime: true,
        ShowBaseScore: true,
        ShowBonusesPenalties: true,
        ShowTopN: 9999
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [openHelpId, setOpenHelpId] = useState(null);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = data.filter(event => event.Enabled);
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID);
                    fetchLeaderboards(enabledEvents[0].EventID);
                    fetchCompetitions(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };

        fetchEvents();
    }, []);

    const fetchLeaderboards = async (eventID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getAllLeaderboards?EventID=${eventID}`);
            const data = await response.json();
            setLeaderboards(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching leaderboards:', error);
        }
    };

    const fetchCompetitions = async (eventID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${eventID}`);
            const data = await response.json();
            setCompetitions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching competitions:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewLeaderboard(prev => ({ ...prev, [name]: value }));
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewLeaderboard({ 
            CompetitionID: '', 
            RefreshInterval: 30, 
            Enabled: true,
            ShowCompetitorID: true,
            ShowCompetitorName: true,
            ShowOrganization: true,
            ShowCompetitorState: true,
            ShowDivision: true,
            ShowAttempt: true,
            ShowTime: true,
            ShowBaseScore: true,
            ShowBonusesPenalties: true,
            ShowTopN: 9999
        });
    };

    const handleAddOrUpdateLeaderboard = async () => {
        if (!newLeaderboard.CompetitionID) {
            alert('Competition ID is required!');
            return;
        }

        const duplicateLeaderboard = leaderboards.find(lb => lb.CompetitionID === newLeaderboard.CompetitionID && lb.EventID === selectedEventID);
        if (duplicateLeaderboard) {
            // Perform update instead of creating a duplicate
            newLeaderboard.LeaderboardID = duplicateLeaderboard.LeaderboardID;
        }

        const leaderboardData = { ...newLeaderboard, EventID: selectedEventID };
        const existingLeaderboard = leaderboards.find(lb => lb.LeaderboardID === newLeaderboard.LeaderboardID);
        const method = existingLeaderboard ? 'PUT' : 'POST';
        const endpoint = existingLeaderboard ? `${API_BASE_URL}/updateLeaderboard` : `${API_BASE_URL}/createLeaderboard`;

        try {
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(leaderboardData)
            });

            if (response.ok) {
                fetchLeaderboards(selectedEventID);
                closeModal();
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to save leaderboard.'}`);
            }
        } catch (error) {
            console.error('Error saving leaderboard:', error);
            alert('An error occurred while saving the leaderboard.');
        }
    };

    const handleDeleteLeaderboard = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this leaderboard?');
        if (!confirmDelete) return;

        try {
            const response = await fetch(`${API_BASE_URL}/deleteLeaderboard?LeaderboardID=${id}`, { method: 'DELETE' });
            if (response.ok) {
                alert('Leaderboard deleted successfully.');
                setLeaderboards(leaderboards.filter(lb => lb.LeaderboardID !== id));
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to delete leaderboard.'}`);
            }
        } catch (error) {
            console.error('Error deleting leaderboard:', error);
        }
    };

    const handleEditLeaderboard = (leaderboard) => {
        setNewLeaderboard(leaderboard);
        openModal();
    };

    const handleCancel = () => {
        setNewLeaderboard({ 
            CompetitionID: '', 
            RefreshInterval: 30, 
            Enabled: true,
            // Reset column visibility settings
            ShowCompetitorID: true,
            ShowCompetitorName: true,
            ShowOrganization: true,
            ShowCompetitorState: true,
            ShowDivision: true,
            ShowAttempt: true,
            ShowTime: true,
            ShowBaseScore: true,
            ShowBonusesPenalties: true,
            ShowTopN: 9999
        });
    };

    // Helper for handling checkbox changes
    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setNewLeaderboard(prev => ({ ...prev, [name]: checked }));
    };

    const toggleHelp = (key) => {
        setOpenHelpId(prev => (prev === key ? null : key));
    };

    return (
        <div className="p-1">
            <div className="flex mb-1">
                <label className="form-control-inline">
                    <select className="form-select" value={selectedEventID || ''} onChange={(e) => {
                        setSelectedEventID(Number(e.target.value));
                        fetchLeaderboards(Number(e.target.value));
                        fetchCompetitions(Number(e.target.value));
                    }}>
                        <option value="">Select Event</option>
                        {events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>
            </div>

            <button className="btn btn-primary mb-1" onClick={openModal}>Add Leaderboard</button>

            {leaderboards.length > 0 && (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Competition</th>
                                <th>Refresh Interval</th>
                                <th>Show Top N</th>
                                <th>Enabled</th>
                                <th>Visible Columns</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboards.map(leaderboard => (
                                <tr key={leaderboard.LeaderboardID}>
                                    <td>{competitions.find(comp => comp.CompetitionID === leaderboard.CompetitionID)?.CompetitionName}</td>
                                    <td>{leaderboard.RefreshInterval}</td>
                                    <td>{leaderboard.ShowTopN === 9999 ? 'All' : leaderboard.ShowTopN}</td>
                                    <td>{leaderboard.Enabled ? 'Yes' : 'No'}</td>
                                    <td>
                                        {[
                                            leaderboard.ShowCompetitorID && 'ID',
                                            leaderboard.ShowCompetitorName && 'Name',
                                            leaderboard.ShowOrganization && 'Org',
                                            leaderboard.ShowCompetitorState && 'State',
                                            leaderboard.ShowDivision && 'Div',
                                            leaderboard.ShowAttempt && 'Attempt',
                                            leaderboard.ShowTime && 'Time',
                                            leaderboard.ShowBaseScore && 'Base',
                                            leaderboard.ShowBonusesPenalties && 'B/P'
                                        ].filter(Boolean).join(', ')}
                                    </td>
                                    <td>
                                        <div className="flex gap-1">
                                            <button className="btn btn-primary" onClick={() => handleEditLeaderboard(leaderboard)}>Edit</button>
                                            <button className="btn btn-danger" onClick={() => handleDeleteLeaderboard(leaderboard.LeaderboardID)}>Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{newLeaderboard.LeaderboardID ? 'Edit Leaderboard' : 'Add New Leaderboard'}</h3>
                        
                        <select
                            className="form-select"
                            name="CompetitionID"
                            value={newLeaderboard.CompetitionID}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select Competition (required)</option>
                            {competitions.map(comp => (
                                <option key={comp.CompetitionID} value={comp.CompetitionID}>{comp.CompetitionName}</option>
                            ))}
                        </select>
                        <label className="form-control-inline">
                            Refresh Interval (Seconds): 
                            <select
                                className="form-select"
                                name="RefreshInterval"
                                value={newLeaderboard.RefreshInterval}
                                onChange={handleInputChange}
                                required
                            >
                                <option value={15}>15</option>
                                <option value={30}>30</option>
                                <option value={60}>60</option>
                                <option value={120}>120</option>
                                <option value={240}>240</option>
                                <option value={480}>480</option>
                                <option value={960}>960</option>
                            </select>
                            <button
                                type="button"
                                className="help-icon"
                                onClick={() => toggleHelp('refreshInterval')}
                                aria-label="Refresh interval help"
                            >
                                ?
                            </button>
                            {openHelpId === 'refreshInterval' && (
                                <span className="help-text">How often the leaderboard will auto-refresh.</span>
                            )}
                        </label>
                        <label className="form-control-inline">
                            Show Top N Competitors: 
                            <input
                                type="number"
                                className="form-control"
                                name="ShowTopN"
                                value={newLeaderboard.ShowTopN}
                                onChange={(e) => setNewLeaderboard(prev => ({ ...prev, ShowTopN: parseInt(e.target.value) || 9999 }))}
                                min="1"
                                max="9999"
                            />
                            <button
                                type="button"
                                className="help-icon"
                                onClick={() => toggleHelp('showTopN')}
                                aria-label="Show top N help"
                            >
                                ?
                            </button>
                            {openHelpId === 'showTopN' && (
                                <span className="help-text">
                                    Show Top N Competitors limits the leaderboard to only show a certain number of competitors. This only applies to individual competitions; 9999 shows all.
                                </span>
                            )}
                        </label>
                        <label className="form-checkbox">
                            <input
                                type="checkbox"
                                name="Enabled"
                                checked={newLeaderboard.Enabled}
                                onChange={(e) => setNewLeaderboard(prev => ({ ...prev, Enabled: e.target.checked }))}
                            />
                            Enabled
                        </label>
                        <div className="form-section">
                            <h3>Column Visibility Settings</h3>
                            <div className="flex flex-wrap gap-1">
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowCompetitorID"
                                        checked={newLeaderboard.ShowCompetitorID}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show ID
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowCompetitorName"
                                        checked={newLeaderboard.ShowCompetitorName}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show Name
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowOrganization"
                                        checked={newLeaderboard.ShowOrganization}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show Organization
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowCompetitorState"
                                        checked={newLeaderboard.ShowCompetitorState}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show State
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowDivision"
                                        checked={newLeaderboard.ShowDivision}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show Division
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowAttempt"
                                        checked={newLeaderboard.ShowAttempt}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show Attempt #
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowTime"
                                        checked={newLeaderboard.ShowTime}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show Time
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowBaseScore"
                                        checked={newLeaderboard.ShowBaseScore}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show Elapsed Seconds
                                </label>
                                <label className="form-checkbox">
                                    <input
                                        type="checkbox"
                                        name="ShowBonusesPenalties"
                                        checked={newLeaderboard.ShowBonusesPenalties}
                                        onChange={handleCheckboxChange}
                                    />
                                    Show Bonuses/Penalties ±
                                </label>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleAddOrUpdateLeaderboard}>
                                Save Leaderboard
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
